import { Button, Callout, Empty } from '@wtfalch/design';
import { cancel, emailHeaders, type MailClient, type Queued, queued } from '@wtfalch/email/mailbox';
import { useCallback, useEffect, useState } from 'react';

import { formatMessageWhen } from '../ui/format.ts';

type Row = Queued & { subject: string };

/**
 * Everything held by a "Send later" pick, soonest first, with a Cancel for
 * each (agora#485) -- `queued()`/`cancel()` already existed on the SDK side;
 * this is the one place in Mail that calls them. `Queued` carries no
 * subject, so a second read (`emailHeaders`) fills that in for display only
 * -- cancelling only ever needs the `submissionId` already in hand.
 */
export function Scheduled({ client }: { client: MailClient }) {
  const [rows, setRows] = useState<readonly Row[] | null>(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setError('');
    queued(client)
      .then(async (list) => {
        const subjects = list.length
          ? await emailHeaders(
              client,
              list.map((q) => q.emailId),
            ).catch(() => null)
          : null;
        const byId = new Map(subjects?.list.map((h) => [h.id, h.subject ?? '']));
        setRows(list.map((q) => ({ ...q, subject: byId.get(q.emailId) || '(no subject)' })));
      })
      .catch((err: unknown) => {
        setRows([]);
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [client]);

  useEffect(load, [load]);

  const onCancel = (row: Row) => {
    setError('');
    setBusyId(row.submissionId);
    cancel(client, row)
      .then(load)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setBusyId(null));
  };

  if (rows === null) return null;

  return (
    <div className="flex flex-col gap-4 mail-set-form">
      <p className="text-sm text-muted m-0">
        Mail you asked to send later. Cancel keeps it in Drafts.
      </p>
      {error && <Callout tone="bad">{error}</Callout>}
      {rows.length === 0 ? (
        <Empty icon="mail">Nothing is waiting to send.</Empty>
      ) : (
        <ul className="flex flex-col gap-2 mail-scheduled-list">
          {rows.map((row) => (
            <li key={row.submissionId} className="flex items-center gap-3 mail-scheduled-row">
              <div className="flex flex-col min-w-0 grow">
                <span className="text-sm truncate">{row.subject}</span>
                <span className="text-sm text-muted">Sends {formatMessageWhen(row.sendAt)}</span>
              </div>
              <Button
                size="sm"
                disabled={busyId === row.submissionId}
                onPress={() => onCancel(row)}
              >
                Cancel
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
